/**
 * The manager's timesheet queue, with bulk approve.
 *
 * Bulk approve is a real requirement, not a convenience: a delivery manager with thirty
 * consultants cannot click thirty times every fortnight, and if they have to, they stop
 * checking and approve blind. The route approves each sheet through the same path and
 * audits each on its own line, and it REPORTS refusals rather than silently dropping
 * them — so this screen must show those refusals, not just a success count.
 */
import { useState } from 'react';
import { CheckCheck, CheckCircle2, XOctagon } from 'lucide-react';

import { useApproveTimesheet, usePendingTimesheets } from '@frontend/api/hooks/timesheets';
import type { Timesheet } from '@shared/types';
import { formatDateRange, formatHours, formatRelative } from '@frontend/lib/format';
import { Button } from '@frontend/components/ui/Button';
import { DataTable, type Column } from '@frontend/components/ui/DataTable';
import { Textarea } from '@frontend/components/ui/Field';
import { Modal } from '@frontend/components/ui/Modal';
import { Banner, PageHeader } from '@frontend/components/ui/Page';
import { StatusBadge } from '@frontend/components/ui/StatusBadge';
import { EmptyState, ErrorState, LoadingRows } from '@frontend/components/ui/States';
import { useToast } from '@frontend/components/ui/Toast';

export function TimesheetApprovalsPage(): JSX.Element {
  const query = usePendingTimesheets();
  const { approve, reject, bulkApprove } = useApproveTimesheet();
  const toast = useToast();

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [rejecting, setRejecting] = useState<Timesheet | null>(null);
  const [reason, setReason] = useState('');
  const [refusals, setRefusals] = useState<{ timesheetId: string; message: string }[]>([]);

  const columns: Column<Timesheet>[] = [
    {
      key: 'employee',
      header: 'Employee',
      cell: (row) => <span className="font-medium text-content">{row.employeeId}</span>,
    },
    {
      key: 'period',
      header: 'Period',
      cell: (row) => formatDateRange(row.periodStart, row.periodEnd),
      sort: (a, b) => a.periodStart.localeCompare(b.periodStart),
    },
    { key: 'total', header: 'Total', numeric: true, cell: (row) => formatHours(row.totalHours) },
    {
      key: 'billable',
      header: 'Billable',
      numeric: true,
      hideOnMobile: true,
      cell: (row) => formatHours(row.billableHours),
    },
    {
      key: 'overtime',
      header: 'Overtime',
      numeric: true,
      cell: (row) =>
        row.isOvertime ? (
          <StatusBadge status="OVERDUE" tone="overdue" label={`${formatHours(row.overtimeHours)} OT`} />
        ) : (
          <span className="text-content-tertiary">—</span>
        ),
    },
    {
      key: 'submitted',
      header: 'Submitted',
      hideOnMobile: true,
      cell: (row) => <span className="text-content-tertiary">{formatRelative(row.submittedAt)}</span>,
      sort: (a, b) => (a.submittedAt ?? '').localeCompare(b.submittedAt ?? ''),
    },
    {
      key: 'actions',
      header: '',
      cell: (row) => (
        <div className="flex items-center justify-end gap-1.5">
          <Button
            size="xs"
            variant="dangerGhost"
            leadingIcon={<XOctagon className="h-3.5 w-3.5" />}
            onClick={() => setRejecting(row)}
            data-testid={`reject-ts-${row.id}`}
          >
            Reject
          </Button>
          <Button
            size="xs"
            variant="primary"
            leadingIcon={<CheckCircle2 className="h-3.5 w-3.5" />}
            onClick={() =>
              approve.mutate(
                { id: row.id },
                {
                  onSuccess: () => toast.success('Timesheet approved'),
                  onError: (error) => toast.fromError(error, 'The approval was not recorded'),
                },
              )
            }
            data-testid={`approve-ts-${row.id}`}
          >
            Approve
          </Button>
        </div>
      ),
    },
  ];

  const rows = query.data?.timesheets ?? [];

  return (
    <div>
      <PageHeader
        title="Timesheet approvals"
        description={
          query.data === undefined
            ? undefined
            : `Submitted timesheets within your ${query.data.scope} scope, oldest first.`
        }
        actions={
          selected.size > 0 ? (
            <Button
              variant="primary"
              leadingIcon={<CheckCheck className="h-3.5 w-3.5" />}
              isLoading={bulkApprove.isPending}
              onClick={() =>
                bulkApprove.mutate(
                  { timesheetIds: [...selected] },
                  {
                    onSuccess: (result) => {
                      setSelected(new Set());
                      setRefusals(result.refused ?? []);
                      const approvedCount = result.approved?.length ?? 0;
                      toast.success(
                        `${approvedCount} timesheet${approvedCount === 1 ? '' : 's'} approved`,
                        (result.refused?.length ?? 0) > 0
                          ? `${result.refused?.length} could not be approved — see the note above the table.`
                          : undefined,
                      );
                    },
                    onError: (error) => toast.fromError(error, 'The bulk approval failed'),
                  },
                )
              }
              data-testid="bulk-approve-timesheets"
            >
              Approve {selected.size} selected
            </Button>
          ) : undefined
        }
      />

      {refusals.length > 0 && (
        <Banner tone="warning" className="mb-3" title={`${refusals.length} were not approved`}>
          <ul className="list-disc space-y-0.5 pl-4">
            {refusals.map((refusal) => (
              <li key={refusal.timesheetId}>
                <code className="code-ref">{refusal.timesheetId}</code>: {refusal.message}
              </li>
            ))}
          </ul>
        </Banner>
      )}

      {query.isLoading ? (
        <LoadingRows rows={6} columns={7} />
      ) : query.isError ? (
        <ErrorState
          error={query.error}
          onRetry={() => {
            void query.refetch();
          }}
        />
      ) : (
        <div className="surface-card">
          <DataTable
            rows={rows}
            columns={columns}
            rowKey={(row) => row.id}
            testIdPrefix="ts-approval-row"
            selection={{ selected, onChange: setSelected }}
            emptyState={
              <EmptyState
                title="Nothing to approve"
                description="Timesheets submitted by the people you approve for appear here. If you are expecting one, the period may not have closed yet — the missing-timesheet report shows who has not submitted."
              />
            }
          />
        </div>
      )}

      <Modal
        open={rejecting !== null}
        onClose={() => {
          setRejecting(null);
          setReason('');
        }}
        title="Return this timesheet"
        description={rejecting === null ? undefined : formatDateRange(rejecting.periodStart, rejecting.periodEnd)}
        size="sm"
        footer={
          <>
            <Button
              variant="ghost"
              onClick={() => {
                setRejecting(null);
                setReason('');
              }}
            >
              Cancel
            </Button>
            <Button
              variant="dangerGhost"
              leadingIcon={<XOctagon className="h-3.5 w-3.5" />}
              disabled={reason.trim().length < 3}
              onClick={() => {
                if (rejecting === null) return;
                reject.mutate(
                  { id: rejecting.id, reason: reason.trim() },
                  {
                    onSuccess: () => toast.success('Returned to the employee', 'They can correct it and resubmit.'),
                    onError: (error) => toast.fromError(error, 'The rejection was not recorded'),
                  },
                );
                setRejecting(null);
                setReason('');
              }}
            >
              Return it
            </Button>
          </>
        }
      >
        <label className="block">
          <span className="mb-1 block text-body font-medium text-content-secondary">
            What needs to change? <span className="text-error">*</span>
          </span>
          <Textarea
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            rows={3}
            autoFocus
            placeholder="Name the day and the problem — e.g. 'Wednesday shows 12 hours on ACME-001 but the assignment ended Tuesday.'"
          />
        </label>
      </Modal>
    </div>
  );
}
